import { AntApp } from 'components/ant/AntApp';
import { AntConfigProvider, AntConfigProviderProps } from 'components/ant/AntConfigProvider';
import React, { FC, PropsWithChildren, useMemo } from 'react';
import { getCSSNumberValue, getCSSVarValue } from 'utils/dom/getCSSVarValue';

import s from './AntThemeProvider.module.less';

const tooltip: NonNullable<AntConfigProviderProps['tooltip']> = {
  classNames: {
    root: s.tooltip,
  },
};

const select: NonNullable<AntConfigProviderProps['select']> = {
  classNames: {
    popup: {
      root: s.selectDropdown,
    },
  },
};

const modal: NonNullable<AntConfigProviderProps['modal']> = {
  classNames: {
    container: s.modalContainer,
    header: s.modalHeader,
    footer: s.modalFooter,
    body: s.modalBody,
    root: s.modalRoot,
  },
};

const form: NonNullable<AntConfigProviderProps['form']> = {
  className: s.form,
};

const collapse: NonNullable<AntConfigProviderProps['collapse']> = {
  className: s.collapse,
};

/**
 * Ant Design theme provider.
 *
 * antd 5+ dropped the Less `modifyVars` theming — theme tokens now go through
 * `ConfigProvider theme={{ token }}` (CSS-in-JS). Tokens are read at runtime from the
 * `--x-color-*` design-system variables injected by `@xometry/uikit`'s `XTheme`, so this
 * provider must render inside `XTheme`.
 *
 * Mirrors the setup in the sibling `gsh-app` project.
 */
export const AntThemeProvider: FC<PropsWithChildren> = (props) => {
  const theme = useMemo<NonNullable<AntConfigProviderProps['theme']>>(
    () => ({
      token: {
        borderRadius: getCSSNumberValue('--input-button-border-radius'),
        colorPrimary: getCSSVarValue('--x-color-action'),
        colorLink: getCSSVarValue('--x-color-action'),
        colorLinkHover: getCSSVarValue('--x-color-action-active'),
        colorSplit: getCSSVarValue('--x-color-border'),
        fontFamily: getCSSVarValue('--x_text_ff'),
        colorError: getCSSVarValue('--x-color-action-red'),
      },
      components: {
        Button: {
          defaultShadow: 'none',
          fontWeight: getCSSVarValue('--x_text_fw_semibold'),
        },
        Table: {
          headerBg: getCSSVarValue('--x-color-background'),
          borderColor: getCSSVarValue('--x-color-border'),
          cellFontSizeSM: 13,
          rowSelectedHoverBg: '#dcf0ff',
          headerColor: getCSSVarValue('--x-color-text-secondary'),
        },
        Menu: {
          itemColor: getCSSVarValue('--x-color-text-primary'),
          // oms has no navy token; map to the brand action colour.
          colorHighlight: getCSSVarValue('--x-color-action'),
        },
        Form: {
          itemMarginBottom: 0,
          verticalLabelPadding: '0 0 0 2px',
        },
        DatePicker: {
          borderRadius: getCSSNumberValue('--input-button-border-radius'),
        },
        Dropdown: {
          zIndexPopup: getCSSNumberValue('--z-index-ant-dropdown'),
        },
        Collapse: {
          colorFillAlter: getCSSVarValue('--x-color-background'),
        },
        Tooltip: {
          zIndexPopupBase: getCSSNumberValue('--z-index-ant-tooltip'),
        },
        Modal: {
          zIndexPopupBase: getCSSNumberValue('--z-index-ant-modal'),
        },
        InputNumber: {
          borderRadius: getCSSNumberValue('--input-button-border-radius'),
        },
        Descriptions: {
          itemPaddingBottom: 0,
          labelColor: getCSSVarValue('--x-color-text'),
        },
      },
    }),
    [],
  );

  return (
    <AntConfigProvider theme={theme} tooltip={tooltip} select={select} modal={modal} form={form} collapse={collapse}>
      <AntApp>{props.children}</AntApp>
    </AntConfigProvider>
  );
};
